// src/hooks/useAnimations.js
//
// Premium motion primitives used across the landing page.
//   - useCountUp:       counts from 0 → target when the element enters view
//   - useScrollProgress: returns 0..1 based on element position in viewport
//   - useInViewOnce:    fires once when element enters view
//
// All hooks respect prefers-reduced-motion (return final value instantly).
//
import { useEffect, useRef, useState } from 'react';

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * useCountUp(target, options)
 *   Returns [ref, displayValue].
 *   Attach ref to the DOM node you want observed.
 *   displayValue is a number you can format yourself.
 *
 *   options:
 *     duration  - ms (default 900)
 *     start     - initial value (default 0)
 *     threshold - IntersectionObserver threshold (default 0.35)
 *     decimals  - rounding only — formatting is up to the caller
 */
export function useCountUp(target, opts = {}) {
  const { duration = 900, start = 0, threshold = 0.35 } = opts;
  const ref = useRef(null);
  const [value, setValue] = useState(prefersReducedMotion() ? target : start);
  const triggered = useRef(false);

  useEffect(() => {
    if (prefersReducedMotion()) { setValue(target); return; }
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || triggered.current) return;
      triggered.current = true;

      const startTime = performance.now();
      const delta = target - start;

      function frame(now) {
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / duration);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(start + delta * eased);
        if (t < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }, { threshold });

    observer.observe(node);
    return () => observer.disconnect();
  }, [target, duration, start, threshold]);

  return [ref, value];
}

/**
 * useScrollProgress(ref)
 *   Returns a number 0..1:
 *     0 — element has just entered the bottom of the viewport
 *     1 — element has just left the top of the viewport
 *   Uses rAF throttling, passive listeners.
 */
export function useScrollProgress(ref) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;

    function update() {
      raf = 0;
      const node = ref.current;
      if (!node) return;
      const r = node.getBoundingClientRect();
      const vh = window.innerHeight;
      // Total scroll distance during which the element is on-screen
      const total = r.height + vh;
      // How much of that distance has already been scrolled
      const passed = vh - r.top;
      const p = Math.max(0, Math.min(1, passed / total));
      setProgress(p);
    }

    function onScroll() { if (!raf) raf = requestAnimationFrame(update); }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref]);

  return progress;
}

/**
 * useInViewOnce(threshold)
 *   Returns [ref, inView]. inView flips true the first time the node
 *   enters the viewport and stays true after.
 */
export function useInViewOnce(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (prefersReducedMotion()) { setInView(true); return; }

    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        obs.disconnect();
      }
    }, { threshold });
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, inView];
}

/**
 * useScrollY()
 *   Returns current window.scrollY (rAF throttled).
 *   Use for subtle parallax decorations.
 */
export function useScrollY() {
  const [y, setY] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;
    function update() { raf = 0; setY(window.scrollY); }
    function onScroll() { if (!raf) raf = requestAnimationFrame(update); }
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return y;
}

/**
 * useBootSequence(steps, options)
 *   Drives a multi-step reveal. Returns current step (0..steps).
 *   Once-per-session via sessionStorage if storageKey provided.
 */
export function useBootSequence(steps, opts = {}) {
  const { stepDelay = 220, initialDelay = 80, storageKey = null } = opts;
  const [step, setStep] = useState(() => {
    if (prefersReducedMotion()) return steps;
    if (storageKey && typeof sessionStorage !== 'undefined') {
      try { if (sessionStorage.getItem(storageKey) === 'done') return steps; }
      catch (_) {}
    }
    return 0;
  });

  useEffect(() => {
    if (step >= steps) return;
    if (prefersReducedMotion()) { setStep(steps); return; }

    const timers = [];
    for (let i = step + 1; i <= steps; i++) {
      const delay = initialDelay + (i - step - 1) * stepDelay;
      timers.push(setTimeout(() => setStep(i), delay));
    }
    if (storageKey && typeof sessionStorage !== 'undefined') {
      const final = setTimeout(() => {
        try { sessionStorage.setItem(storageKey, 'done'); } catch (_) {}
      }, initialDelay + (steps - step) * stepDelay);
      timers.push(final);
    }
    return () => timers.forEach(clearTimeout);
  }, []); // run once on mount

  return step;
}
